import React from 'react';

const ProgressModal = ({ show, currentStep, progress, status, onClose }) => {
  if (!show) return null;

  const stepsList = [
    { id: 'approve', name: 'Token Approval', icon: 'fas fa-check-circle' },
    { id: 'payment', name: 'Room Payment', icon: 'fas fa-credit-card' },
  ];

  const getStepStatus = (stepId) => {
    if (currentStep === stepId) return 'active';
    const stepIndex = stepsList.findIndex(s => s.id === stepId);
    const currentIndex = stepsList.findIndex(s => s.id === currentStep);
    return stepIndex < currentIndex ? 'completed' : 'pending';
  };

  const getStepIcon = (stepId) => {
    const stepStatus = getStepStatus(stepId);
    switch (stepStatus) {
      case 'active':
        return 'fas fa-spinner fa-spin';
      case 'completed':
        return 'fas fa-check-circle';
      default:
        return 'fas fa-circle';
    }
  };

  const getStepClasses = (stepId) => {
    const status = getStepStatus(stepId);
    const baseClasses = "flex items-center gap-4 p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ease-out";
    
    switch (status) {
      case 'active':
        return `${baseClasses} bg-gradient-to-r from-blue-500/20 via-purple-500/15 to-blue-500/20 border-blue-400/50 shadow-lg shadow-blue-500/30 transform -translate-y-1`;
      case 'completed':
        return `${baseClasses} bg-gradient-to-r from-emerald-500/20 via-cyan-500/15 to-emerald-500/20 border-emerald-400/50 shadow-lg shadow-emerald-500/30`;
      default:
        return `${baseClasses} bg-gradient-to-r from-gray-600/60 via-gray-500/50 to-gray-600/60 border-gray-400/20 hover:from-gray-500/70 hover:via-gray-400/60 hover:to-gray-500/70`;
    }
  };

  const getIconClasses = (stepId) => {
    const status = getStepStatus(stepId);
    const baseClasses = "w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all duration-300 border-2";
    
    switch (status) {
      case 'active':
        return `${baseClasses} bg-gradient-to-br from-blue-400 via-purple-500 to-blue-600 border-blue-300 text-white shadow-lg shadow-blue-500/60 animate-pulse`;
      case 'completed':
        return `${baseClasses} bg-gradient-to-br from-emerald-400 via-cyan-500 to-emerald-600 border-emerald-300 text-white shadow-lg shadow-emerald-500/60`;
      default:
        return `${baseClasses} bg-gradient-to-br from-gray-500 via-gray-600 to-gray-700 border-gray-400/30 text-white/60 shadow-lg shadow-gray-500/20`;
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/95 via-gray-900/90 to-black/95 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-gradient-to-br from-gray-800/95 via-gray-700/90 to-gray-800/95 border-2 border-blue-500/40 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-black/80 backdrop-blur-xl animate-in zoom-in-95 duration-300 relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-cyan-500/5 opacity-50 animate-pulse"></div>
        
        {/* Glowing border effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 blur-xl opacity-30"></div>
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm">💣</span>
            </div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-lg">
              Payment Progress
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700/50 via-gray-600/50 to-gray-700/50 border-2 border-gray-500/40 text-gray-300 flex items-center justify-center transition-all duration-300 hover:from-gray-600/70 hover:via-gray-500/70 hover:to-gray-600/70 hover:border-gray-400/60 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500/50 shadow-lg backdrop-blur-sm"
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        </div>
        
        <div className="space-y-6 relative z-10">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="h-6 bg-gradient-to-r from-gray-700/80 via-gray-600/80 to-gray-700/80 border-2 border-blue-500/30 rounded-full overflow-hidden shadow-inner relative">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-full transition-all duration-700 ease-out relative shadow-lg shadow-blue-500/60"
                style={{ width: `${progress || 0}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full animate-pulse"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-blue-300/40 via-transparent to-purple-600/40 rounded-full"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-cyan-400/20 to-purple-500/30 rounded-full"></div>
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span className="text-gray-500">0%</span>
              <span className="text-blue-400 font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{progress || 0}%</span>
              <span className="text-gray-500">100%</span>
            </div>
          </div>
          
          {/* Steps */}
          <div className="space-y-4">
            {stepsList.map((step, index) => (
              <div 
                key={step.id} 
                className={getStepClasses(step.id)}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={getIconClasses(step.id)}>
                  <i className={getStepIcon(step.id)}></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white drop-shadow-sm">
                    {step.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
          
          {/* Status */}
          <div className="text-center p-5 bg-gradient-to-r from-gray-700/70 via-gray-600/60 to-gray-700/70 border border-blue-500/30 rounded-xl backdrop-blur-sm shadow-inner">
            <p className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent font-medium drop-shadow-sm">
              {status || 'Initializing...'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressModal;
